<template>
  <div class="sms-code">
    <span v-show="canSendCode" class="sms-code__btn-wrap" @click="onClick">
      <el-button
        v-if="!showCustomBtn"
        :class="['sms-code__btn', isLinkStyle ? 'sms-code__btn--link' : '']"
        :loading="isLoading"
        :loading-text="btnText"
        size="small"
        type="primary"
        v-bind="$attrs"
      >{{ btnText }}</el-button>
      <slot v-else name="button"></slot>
    </span>
    <span class="sms-code__text" :style="`line-height:${countdownTextHeight}px;`" v-show="!canSendCode">{{ countdownText }}({{ countdown }}s)</span>
  </div>
</template>

<script>
import captcha from '@/lib/TCaptcha';
import Request from '@/lib/request';
import { isPhoneNumber, isEmpty } from '@/utils/formRules';

/**
 * 组件：短信验证码按钮业务组件
 *
 * 示例1（默认样式）：
 * <sms-code-button :phone="phone" />
 *
 * 示例2（按钮风格）：
 * <sms-code-button size="mini" type="primary" btnText="获取验证码" phoneErrorTips="请输入正确的手机号" :phone="phone" />
 *
 * 示例3（链接风格）：
 * <sms-code-button size="small" :isLinkStyle="true" countdownText="重新发送" btnText="获取验证码" :phone="phone" />
 *
 * 示例4（自定义按钮）：
    <sms-code-button btnText="获取验证码" :phone="phone" countdownTextHeight="44">
      <template #button>
        <el-button size="small" type="primary">获取验证码</el-button>
      </template>
    </sms-code-button>
 */
export default {
  props: {
    phone: {
      type: String,
      default: '',
    },
    phoneErrorTips: {
      type: String,
      default: '',
    },
    btnText: {
      type: String,
      default: '发送验证码',
    },
    // 是否链接样式，Boolean，true 链接样式；false 按钮样式，默认
    isLinkStyle: {
      type: Boolean,
      default: false,
    },
    countdownText: {
      type: String,
      default: '重新发送',
    },
    countdownTextHeight: {
      type: String,
      default: '32',
    },
  },
  data() {
    return {
      isLoading: false,
      timer: null, // 计时器
      countdown: 60, // 计时，秒
      canSendCode: true, // 是否可以发送验证码
    };
  },
  computed: {
    showCustomBtn() {
      return Boolean(this.$slots.button);
    },
  },
  methods: {
    onClick() {
      if (!this.checkPhone()) return;
      if (this.isLoading) return;
      this.showGraphicCaptcha();
    },
    checkPhone() {
      if (isEmpty(this.phone)) {
        this.$message.error(this.phoneErrorTips ? this.phoneErrorTips : '请输入手机号');
        return false;
      };
      if (!isPhoneNumber(this.phone)) {
        this.$message.error(this.phoneErrorTips ? this.phoneErrorTips : '请输入正确的手机号');
        return false;
      };

      return true;
    },
    // 显示图形验证码
    showGraphicCaptcha() {
      captcha.show({
        callback: (res) => {
          if (res.ret === 2) {
            // 用户主动关闭验证码
            return;
          }

          if (res.ret !== 0) {
            this.$message.error(`${res.errorMessage}。错误码：${res.errorCode}`);
            return;
          }

          this.sendCode({
            ticket: res.ticket,
            randstr: res.randstr,
          });
        },
      });
    },
    sendCode({ ticket, randstr }) {
      if (this.isLoading) return;

      this.isLoading = true;
      this.$emit('onLoading');
      const reqData = {
        phone_number: this.phone,
        ticket,
        randstr,
      };
      Request({
        url: 'v1/account/smscode/send_verify',
        method: 'post',
        data: reqData,
        autoErrorTips: false,
      })
        .then((res) => {
          const code = res?.head?.code;
          if (Number(code) !== 0) {
            const errMsg = `发送失败：${res?.head?.msg}`;
            this.$message.error(errMsg);
            this.$emit('onError', errMsg);
            return;
          }
          this.$message.success('发送成功');
          this.$emit('onSuccess');
          this.startTiming();
        })
        .catch((err) => {
          const errMsg = `发送失败：${err.message}`;
          this.$message.error(errMsg);
          this.$emit('onError', errMsg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    startTiming() {
      this.countdown = 60;
      this.canSendCode = false;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (this.countdown <= 0) {
          clearInterval(this.timer);
          this.timer = null;
          this.canSendCode = true;
        } else {
          this.countdown -= 1;
        }
      }, 1000);
    },
  },
};
</script>

<style lang="less" scoped>
.sms-code {
  display: inline-flex;
  align-items: center;
}
.sms-code__btn-wrap {
  display: inline-block;
}
.sms-code__btn--link {
    background: transparent;
    border: 0 none;
    padding: 0;
    color: var(--ui-main-color);
  }
.sms-code__text {
  display: inline-block;
  white-space: nowrap;
  color: #979797;
}
</style>
